import {
  brandOptions,
  organizations,
  suppliers,
  adminUnits,
  contactPersons,
} from './itemsFixtureData';

/**
 * Deterministic fixture rows for the dl-58423 bulk-edit **atomicity part 2** epic (DL-65614 —
 * success path, scale, cache freshness & adjacent flows / Testmo folder 80845). Split into its own
 * file to keep `items.ts` under the 800-line cap, mirroring `itemsBulkEditProbeFixtureData.ts`.
 *
 * Every group is isolated by a unique `?search=<token>` substring (the mock free-text-searches
 * name + userDefinedId, `applyFilters` in `items.ts`) so each test owns its own rows and cannot
 * collide with another Playwright worker mutating the SHARED server-side `itemsData` array. Each
 * group carries at least one **unselected control** row so a write that touched every row in the
 * table (rather than only the selection) would fail. Baselines are known, distinct and non-empty
 * per the epic's requirement, so an untouched field can be asserted unchanged.
 *
 * Rows are pushed onto the real mutable `itemsData` array (not injected via `apiTransform`), so the
 * bulk `PUT /api/items` handler's in-place mutation is what a subsequent GET / `page.reload()`
 * reflects — a real round trip, which is the only mock-mode-provable half of this folder
 * (cache coherence), never the backend DB rollback (see DL-65589).
 */

type AtomicityItem = {
  id: number;
  name: string;
  userDefinedId: string;
  description: string | null;
  brand: (typeof brandOptions)[number];
  organization: (typeof organizations)[number];
  supplier: (typeof suppliers)[number] | null;
  companyContactPerson: (typeof contactPersons)[number] | null;
  supplierContactPerson: null;
  adminUnit: (typeof adminUnits)[number];
  logo: null;
  isActive: boolean;
  suppliedBy: null;
  readyForExport: boolean;
  updatedAfterLastExport: boolean;
  lastUpdated: string;
  deadline: null;
  etd: null;
  erpItemId: null;
  defaultQty: number;
  nameGeneratorExtraText: null;
  physicalReferenceNumber: null;
  categories: never[];
  customFields: never[];
};

const atomicityItem = (
  id: number,
  name: string,
  overrides: Partial<Pick<AtomicityItem, 'isActive' | 'description' | 'defaultQty' | 'brand'>> = {}
): AtomicityItem => ({
  id,
  name,
  userDefinedId: `ATOM-${id}`,
  description: `${name} baseline description`,
  brand: brandOptions[0],
  organization: organizations[0],
  supplier: suppliers[0],
  companyContactPerson: contactPersons[0],
  supplierContactPerson: null,
  adminUnit: adminUnits[0],
  logo: null,
  isActive: true,
  suppliedBy: null,
  readyForExport: false,
  updatedAfterLastExport: false,
  lastUpdated: '2026-02-20T09:00:00',
  deadline: null,
  etd: null,
  erpItemId: null,
  defaultQty: 1,
  nameGeneratorExtraText: null,
  physicalReferenceNumber: null,
  categories: [],
  customFields: [],
  ...overrides,
});

// Ids live in the 91000–91199 band, kept clear of the 90400-range rows other item fixtures
// (placeholderMixedValues, decimalQtyProbe) already seed — a duplicate id makes the bulk-PUT
// `findIndex` and the GET `find` resolve to different rows, so an "untouched field" assertion reads
// a stale/blank sibling instead of the mutated row.

// 655259 (DL-65616) — happy-path Brand change on 3 items; all share brand[0] so the modal offers a
// different brand, and the Control keeps brand[0]. Search token: "Atomicity Journey".
// Ids 91005–91008 (NOT 91000–91004): the merged DL-65589 fixture (bulkAtomicityFixtureData.ts)
// already seeds ids 91001–91004, and a duplicate id makes the bulk-PUT findIndex / GET find resolve
// to the wrong row across the two epics' probe sets.
export const atomicityJourneyRows: AtomicityItem[] = [
  atomicityItem(91005, 'Atomicity Journey Alpha'),
  atomicityItem(91006, 'Atomicity Journey Bravo'),
  atomicityItem(91007, 'Atomicity Journey Charlie'),
  atomicityItem(91008, 'Atomicity Journey Control'),
];

// 655282 (DL-65618) — end-to-end atomicity walk (confirm summary + toast) on 3 items; Brand change.
// Search token: "Atomicity Endflow".
export const atomicityEndflowRows: AtomicityItem[] = [
  atomicityItem(91010, 'Atomicity Endflow Delta'),
  atomicityItem(91011, 'Atomicity Endflow Echo'),
  atomicityItem(91012, 'Atomicity Endflow Foxtrot'),
  atomicityItem(91013, 'Atomicity Endflow Control'),
];

// 655262 (DL-65621) — single-item Status Active→Inactive (all start Active → modal offers Inactive).
// Search token: "Atomicity Single".
export const atomicitySingleRows: AtomicityItem[] = [
  atomicityItem(91020, 'Atomicity Single Solo', { isActive: true }),
  atomicityItem(91021, 'Atomicity Single Control', { isActive: true }),
];

// 655264 (DL-65625) — commit reflects updated values: Status Inactive→Active on 2 items (all start
// Inactive → modal offers Active). Search token: "Atomicity Commit".
export const atomicityCommitRows: AtomicityItem[] = [
  atomicityItem(91030, 'Atomicity Commit Golf', { isActive: false }),
  atomicityItem(91031, 'Atomicity Commit Hotel', { isActive: false }),
  atomicityItem(91032, 'Atomicity Commit Control', { isActive: false }),
];

// 655281 (DL-65626) — table auto-refreshes after success (cache invalidation): Brand change on 2.
// Search token: "Atomicity Refresh".
export const atomicityRefreshRows: AtomicityItem[] = [
  atomicityItem(91040, 'Atomicity Refresh India'),
  atomicityItem(91041, 'Atomicity Refresh Juliet'),
  atomicityItem(91042, 'Atomicity Refresh Control'),
];

// 655269 (DL-65627) — a second bulk edit reads fresh data: first change Brand, then Status on the
// same 2 items (start Active so the 2nd edit offers Inactive). Search token: "Atomicity Second".
export const atomicitySecondRows: AtomicityItem[] = [
  atomicityItem(91050, 'Atomicity Second Kilo', { isActive: true }),
  atomicityItem(91051, 'Atomicity Second Lima', { isActive: true }),
  atomicityItem(91052, 'Atomicity Second Control', { isActive: true }),
];

// 655272 (DL-65628) — single-item inline Description edit still works. Search token: "Atomicity Inline".
export const atomicityInlineRows: AtomicityItem[] = [
  atomicityItem(91060, 'Atomicity Inline Mike', {
    description: 'Atomicity inline original description',
  }),
];

// 655263 (DL-65623) — maximum allowed selection = 50 (`maxItemsBulkEditSelection`,
// src/features/misc/table/bulkEdit/validation/bulkEdit.ts). Exactly 50 rows so page 1 (pageSize 50)
// holds them all and the table's "Select all" tile selects precisely the max. All start Inactive so
// the modal offers Active. Search token "Atomicity Max" returns exactly these 50 (no other row's
// name contains it); the control is named so it does NOT contain "Atomicity Max" and is verified
// under its own token "Atomicity Fifty Control".
export const atomicityMaxRows: AtomicityItem[] = Array.from({ length: 50 }, (_, i) =>
  atomicityItem(91100 + i, `Atomicity Max ${String(i + 1).padStart(2, '0')}`, { isActive: false })
);
export const atomicityMaxControlRow: AtomicityItem = atomicityItem(
  91199,
  'Atomicity Fifty Control',
  {
    isActive: false,
  }
);

export const atomicityProbeRows: AtomicityItem[] = [
  ...atomicityJourneyRows,
  ...atomicityEndflowRows,
  ...atomicitySingleRows,
  ...atomicityCommitRows,
  ...atomicityRefreshRows,
  ...atomicitySecondRows,
  ...atomicityInlineRows,
  ...atomicityMaxRows,
  atomicityMaxControlRow,
];
